import React, { Component } from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Checklist } from '../../components/Checklist';
import { COMMUNICATION_CHECKLIST } from '../../utils/constants';
import { MessageSquare, Lock, Mail, Phone, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
export function Communication() {
  return <Layout data-id="element-4483">
      <div className="container mx-auto px-4 py-12" data-id="element-4484">
        {/* Hero */}
        <div className="mb-12" data-id="element-4485">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium mb-4 border border-blue-500/20" data-id="element-4486">
            <MessageSquare className="h-3 w-3 mr-2" data-id="element-4487" />
            Personal Protocol
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4488">
            Secure Communication
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4489">
            Ensure your private conversations stay private. Metadata reveals
            patterns even if content is encrypted.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4490">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4491">
            {/* Why This Matters */}
            <section data-id="element-4492">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4493">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-4494">
                <p className="text-slate-300 mb-4" data-id="element-4495">
                  Communication is the lifeblood of our digital lives, but it's
                  also the most surveilled. Governments, corporations, and
                  criminals routinely intercept messages and analyze metadata.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-4496">
                  <li className="flex items-start" data-id="element-4497">
                    <span className="text-blue-500 mr-2" data-id="element-4498">•</span> SMS is
                    completely insecure and easily intercepted
                  </li>
                  <li className="flex items-start" data-id="element-4499">
                    <span className="text-blue-500 mr-2" data-id="element-4500">•</span> Email is often
                    stored in plain text by providers
                  </li>
                  <li className="flex items-start" data-id="element-4501">
                    <span className="text-blue-500 mr-2" data-id="element-4502">•</span> Metadata (who,
                    when, where) can be more revealing than content
                  </li>
                </ul>
              </div>
            </section>

            {/* Core Components */}
            <section data-id="element-4503">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4504">
                Core Components
              </h2>
              <div className="grid gap-6" data-id="element-4505">
                <ComponentCard title="Encrypted Messaging" desc="Use End-to-End Encrypted (E2E) apps like Signal. Avoid SMS and unencrypted messengers." icon={Lock} data-id="element-4506" />
                <ComponentCard title="Email Security" desc="Secure your primary email with MFA. Consider privacy-focused providers like ProtonMail." icon={Mail} data-id="element-4507" />
                <ComponentCard title="Voice & Video Calls" desc="Use E2E encrypted calling (Signal, FaceTime). Regular phone calls are easily intercepted." icon={Phone} data-id="element-4508" />
                <ComponentCard title="Metadata Awareness" desc="Understand that even with encryption, metadata (who you talk to and when) is visible." icon={AlertTriangle} data-id="element-4509" />
              </div>
            </section>

            {/* Implementation Steps */}
            <section data-id="element-4510">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4511">
                Implementation Steps
              </h2>
              <div className="space-y-4" data-id="element-4512">
                <Step number={1} text="Install Signal or another reputable E2E encrypted messaging app." data-id="element-4513" />
                <Step number={2} text="Migrate sensitive conversations to encrypted platforms." data-id="element-4514" />
                <Step number={3} text="Enable MFA on your primary email account immediately." data-id="element-4515" />
                <Step number={4} text="Audit email forwarding rules to ensure no unauthorized monitoring." data-id="element-4516" />
                <Step number={5} text="Educate your close contacts on why secure communication matters." data-id="element-4517" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4518">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4519">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-4520">
                <ul className="space-y-3 text-slate-300" data-id="element-4521">
                  <li className="flex items-start" data-id="element-4522">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4523" />
                    Using SMS for 2FA codes or sensitive conversations.
                  </li>
                  <li className="flex items-start" data-id="element-4524">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4525" />
                    Trusting "secure" apps without verifying they use E2E
                    encryption.
                  </li>
                  <li className="flex items-start" data-id="element-4526">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4527" />
                    Ignoring metadata—knowing who you talk to is often enough
                    for adversaries.
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4528">
            <Card className="p-6 sticky top-24" data-id="element-4529">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-4530">
                Communication Checklist
              </h3>
              <Checklist items={COMMUNICATION_CHECKLIST} id="personal-communication" data-id="element-4531" />

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-4532">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-4533">
                  Next Steps
                </h4>
                <Link to="/personal/internet" className="block w-full" data-id="element-4534">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-slate-950" data-id="element-4535">
                    Next: Internet Safety{' '}
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-4536" />
                  </Button>
                </Link>
                <Link to="/tools/privacy" className="block w-full mt-3" data-id="element-4537">
                  <Button variant="secondary" className="w-full" data-id="element-4538">
                    Privacy Tools
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ComponentCard({
  title,
  desc,
  icon: Icon
}: {
  title: string;
  desc: string;
  icon: any;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 flex items-start" data-id="element-4539">
      <div className="bg-blue-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-4540">
        <Icon className="h-6 w-6 text-blue-500" data-id="element-4541" />
      </div>
      <div data-id="element-4542">
        <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4543">{title}</h3>
        <p className="text-slate-400 text-sm" data-id="element-4544">{desc}</p>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-4545">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-blue-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-4546">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-4547">{text}</p>
    </div>;
}